/**
 * Browser Fingerprint Utility
 * 
 * Generates and persists a unique browser ID for audit logging purposes.
 * This ID persists across sessions and should NOT be cleared during logout.
 * 
 * Use cases:
 * - Track if multiple users are using the same browser
 * - Track if a user is logged into multiple browsers simultaneously
 */

const BROWSER_FINGERPRINT_KEY = 'portalBrowserFingerprint';
const BROWSER_DETAILS_KEY = 'portalBrowserDetails';

// Flag to prevent recursive audit log calls
let isLoggingBrowserDetails = false;

// Fields that change frequently and should be ignored when comparing
const VOLATILE_FIELDS = [
  'viewportSize',
  'windowSize', 
  'zoomLevel',
  'visualViewportScale',
  'screenOrientation',
  'connectionDownlink',
  'onLine',
  'referrer',
  'historyLength',
];

/**
 * Generates a unique browser fingerprint ID
 * Format: 8-character alphanumeric string (base36)
 * Provides 2.8 trillion combinations - more than enough for 100K browsers
 */
const generateFingerprint = () => {
  return Math.random().toString(36).substring(2, 10);
};

/**
 * Collects browser details for audit logging
 */
const getBrowserDetails = () => {
  const nav = navigator || {};
  const screen = window.screen || {};
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection || {};
  
  return {
    // Browser identification
    userAgent: nav.userAgent || 'unknown',
    platform: nav.platform || 'unknown',
    vendor: nav.vendor || 'unknown',
    
    // Language & locale
    language: nav.language || 'unknown',
    languages: nav.languages ? nav.languages.join(', ') : 'unknown',
    
    // Privacy settings
    cookiesEnabled: nav.cookieEnabled,
    doNotTrack: nav.doNotTrack,
    
    // Screen & display
    screenResolution: `${screen.width || 0}x${screen.height || 0}`,
    screenAvailable: `${screen.availWidth || 0}x${screen.availHeight || 0}`,
    screenColorDepth: screen.colorDepth || 'unknown',
    devicePixelRatio: window.devicePixelRatio || 1,
    screenOrientation: (screen.orientation && screen.orientation.type) || (window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'),
    
    // Viewport & window
    viewportSize: `${window.innerWidth || 0}x${window.innerHeight || 0}`,
    windowSize: `${window.outerWidth || 0}x${window.outerHeight || 0}`,
    
    // Zoom level detection
    zoomLevel: Math.round((window.outerWidth / window.innerWidth) * 100) || 100,
    visualViewportScale: window.visualViewport ? window.visualViewport.scale : 1,
    
    // Time & location
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
    timezoneOffset: new Date().getTimezoneOffset(),
    
    // Device capabilities
    touchSupport: 'ontouchstart' in window || nav.maxTouchPoints > 0,
    maxTouchPoints: nav.maxTouchPoints || 0,
    hardwareConcurrency: nav.hardwareConcurrency || 'unknown',
    deviceMemory: nav.deviceMemory ? `${nav.deviceMemory}GB` : 'unknown',
    
    // Network info (if available)
    connectionType: conn.effectiveType || 'unknown',
    connectionDownlink: conn.downlink ? `${conn.downlink}Mbps` : 'unknown',
    onLine: nav.onLine,
    
    // Automation detection
    webdriver: nav.webdriver || false,
    
    // PDF viewer
    pdfViewerEnabled: nav.pdfViewerEnabled,
    
    // Media capabilities (relevant for consultations)
    mediaDevicesSupported: !!nav.mediaDevices,
    
    // Notification permission
    notificationPermission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
    
    // User preferences
    prefersDarkMode: window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches,
    prefersReducedMotion: window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    
    // Session info
    referrer: document.referrer || 'direct',
    historyLength: window.history.length || 0,
    
    // Storage support
    localStorageSupported: !!window.localStorage,
    sessionStorageSupported: !!window.sessionStorage,
    indexedDBSupported: !!window.indexedDB,
  };
};

/**
 * Writes audit log for new browser registration (deferred to avoid circular calls)
 */
const logNewBrowserRegistration = (fingerprint) => {
  // Defer to next tick to avoid circular dependency with writeAuditLog
  setTimeout(async () => {
    if (isLoggingBrowserDetails) return;
    isLoggingBrowserDetails = true;
    
    try {
      // Dynamic import to avoid circular dependency
      const { writeAuditLog } = await import('../components/common/apis');
      const browserDetails = getBrowserDetails();
      
      // Store initial browser details
      localStorage.setItem(BROWSER_DETAILS_KEY, JSON.stringify(browserDetails));
      
      // Include browser details directly in description to ensure they're logged
      const detailsJson = JSON.stringify(browserDetails);
      await writeAuditLog(
        `New browser registered with fingerprint: ${fingerprint} [BROWSER_DETAILS:${detailsJson}]`,
        'Browser Registration',
        {},
        {},
        { deferMs: 0, wait: true }
      );
    } catch (error) {
      console.warn('Failed to log browser registration:', error);
    } finally {
      isLoggingBrowserDetails = false;
    }
  }, 100);
};

/**
 * Gets the existing browser fingerprint or creates a new one if it doesn't exist.
 * This ID should persist permanently in localStorage.
 * @returns {string} The browser fingerprint ID
 */
export const getBrowserFingerprint = () => {
  let fingerprint = localStorage.getItem(BROWSER_FINGERPRINT_KEY);
  
  if (!fingerprint) {
    fingerprint = generateFingerprint();
    localStorage.setItem(BROWSER_FINGERPRINT_KEY, fingerprint);
    // Log new browser registration with details
    logNewBrowserRegistration(fingerprint);
  }
  
  return fingerprint;
};

/**
 * Compares two objects and returns the differences (excluding volatile fields)
 */
const getChangedFields = (oldDetails, newDetails) => {
  const changes = {};
  
  for (const key of Object.keys(newDetails)) {
    if (VOLATILE_FIELDS.includes(key)) continue;
    
    const oldVal = oldDetails[key];
    const newVal = newDetails[key];
    
    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      changes[key] = { from: oldVal, to: newVal };
    }
  }
  
  return Object.keys(changes).length > 0 ? changes : null;
};

/**
 * Check for browser detail changes after login.
 * Call this after login with a delay to allow time for permission prompts.
 * @param {number} delayMs - Delay in milliseconds before checking (default: 60000 = 1 minute)
 */
export const checkBrowserDetailsAfterLogin = (delayMs = 60000) => {
  setTimeout(async () => {
    if (isLoggingBrowserDetails) return;
    
    try {
      const currentDetails = getBrowserDetails();
      const storedDetailsJson = localStorage.getItem(BROWSER_DETAILS_KEY);
      
      if (!storedDetailsJson) {
        // First time storing details
        localStorage.setItem(BROWSER_DETAILS_KEY, JSON.stringify(currentDetails));
        return;
      }
      
      const storedDetails = JSON.parse(storedDetailsJson);
      const changes = getChangedFields(storedDetails, currentDetails);
      
      if (changes) {
        isLoggingBrowserDetails = true;
        
        try {
          const { writeAuditLog } = await import('../components/common/apis');
          const fingerprint = getBrowserFingerprint();
          
          // Include changes directly in description to ensure they're logged
          const changesJson = JSON.stringify({ changes, currentDetails });
          await writeAuditLog(
            `Browser settings changed for fingerprint: ${fingerprint} [BROWSER_CHANGES:${changesJson}]`,
            'Browser Settings Change',
            {},
            {},
            { deferMs: 0, wait: true }
          );
        } finally {
          isLoggingBrowserDetails = false;
        }
        
        // Update stored details after logging
        localStorage.setItem(BROWSER_DETAILS_KEY, JSON.stringify(currentDetails));
      }
    } catch (error) {
      console.warn('Failed to check browser details:', error);
    }
  }, delayMs);
};

/**
 * The localStorage keys used for browser tracking.
 * Export these so they can be added to preserved keys during logout.
 */
export const BROWSER_FINGERPRINT_STORAGE_KEY = BROWSER_FINGERPRINT_KEY;
export const BROWSER_DETAILS_STORAGE_KEY = BROWSER_DETAILS_KEY;

export default getBrowserFingerprint;



// WEBPACK FOOTER //
// ./src/utils/browserFingerprint.js